import { ClinicMapLocation } from '@chiroup/core/types/ClinicMapLocation.type';
import { useCallback, useMemo, useState } from 'react';
import { ColorByOption } from './ClinicMapView';
import {
  CustomColors,
  DEFAULT_STATUS,
  getStateName,
  normalizeStateToAbbrev,
  PLAN_OPTIONS,
  POST_TYPE_OPTIONS,
  POSTING_PLATFORM_OPTIONS,
  POSTING_SCHEDULE_OPTIONS,
  StateInfo,
  STATUS_OPTIONS,
} from './constants';

type FilterMode = 'and' | 'or' | 'custom';

export interface FilterRelations {
  plan: 'and' | 'or';
  status: 'and' | 'or';
  date: 'and' | 'or';
  schedule: 'and' | 'or';
  platform: 'and' | 'or';
  postType: 'and' | 'or';
  states: 'and' | 'or';
}

const DEFAULT_FILTER_RELATIONS: FilterRelations = {
  plan: 'and',
  status: 'and',
  date: 'and',
  schedule: 'and',
  platform: 'and',
  postType: 'and',
  states: 'and',
};

const DEFAULT_CUSTOM_COLORS: CustomColors = {
  plan: {},
  status: {},
  schedule: {},
  postType: {},
  platform: {},
};

/**
 * Custom hook for managing clinic map filters
 * Handles filter state, filtering logic, and derived data like available states
 */
const useClinicMapFilters = (locations: ClinicMapLocation[]) => {
  // Filter selections
  const [selectedPlans, setSelectedPlans] = useState<string[]>(PLAN_OPTIONS.map(p => p.value));
  const [selectedStatus, setSelectedStatus] = useState<string[]>(DEFAULT_STATUS);
  const [selectedSchedules, setSelectedSchedules] = useState<string[]>(
    POSTING_SCHEDULE_OPTIONS.map(s => s.value)
  );
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(
    POSTING_PLATFORM_OPTIONS.map(p => p.value)
  );
  const [selectedPostTypes, setSelectedPostTypes] = useState<string[]>(
    POST_TYPE_OPTIONS.map(t => t.value)
  );
  const [selectedStatesFilter, setSelectedStatesFilter] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Filter mode and relations
  const [filterMode, setFilterMode] = useState<FilterMode>('and');
  const [filterRelations, setFilterRelations] = useState<FilterRelations>(DEFAULT_FILTER_RELATIONS);

  // Search
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statesFilterSearch, setStatesFilterSearch] = useState<string>('');

  // Color customization
  const [colorBy, setColorBy] = useState<ColorByOption>('plan');
  const [customColors, setCustomColors] = useState<CustomColors>(DEFAULT_CUSTOM_COLORS);

  const handleColorChange = useCallback((category: ColorByOption, value: string, color: string) => {
    setCustomColors(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [value]: color,
      },
    }));
  }, []);

  // Get unique states with counts, sorted by full name
  const availableStates = useMemo((): StateInfo[] => {
    const stateCounts: Record<string, number> = {};
    locations.forEach(loc => {
      const normalizedState = normalizeStateToAbbrev(loc.state);
      if (normalizedState) {
        stateCounts[normalizedState] = (stateCounts[normalizedState] || 0) + 1;
      }
    });

    return Object.entries(stateCounts)
      .map(([abbrev, count]) => ({
        abbrev,
        fullName: getStateName(abbrev),
        count,
      }))
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [locations]);

  // Filtered states for geographic filter search
  const filteredFilterStates = useMemo((): StateInfo[] => {
    if (!statesFilterSearch.trim()) return availableStates;
    const query = statesFilterSearch.toLowerCase().trim();
    return availableStates.filter(
      state =>
        state.fullName.toLowerCase().includes(query) || state.abbrev.toLowerCase().includes(query)
    );
  }, [availableStates, statesFilterSearch]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedPlans.length < PLAN_OPTIONS.length) count++;
    if (selectedStatus.length < STATUS_OPTIONS.length) count++;
    if (selectedSchedules.length < POSTING_SCHEDULE_OPTIONS.length) count++;
    if (selectedPlatforms.length < POSTING_PLATFORM_OPTIONS.length) count++;
    if (selectedPostTypes.length < POST_TYPE_OPTIONS.length) count++;
    if (selectedStatesFilter.length > 0) count++;
    if (startDate || endDate) count++;
    return count;
  }, [
    selectedPlans,
    selectedStatus,
    selectedSchedules,
    selectedPlatforms,
    selectedPostTypes,
    selectedStatesFilter,
    startDate,
    endDate,
  ]);

  // Main filter logic
  const filteredLocations = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    // Check if each filter type is "active" (user made a specific selection)
    const isPlanFilterActive =
      selectedPlans.length < PLAN_OPTIONS.length && selectedPlans.length > 0;
    const isStatusFilterActive =
      selectedStatus.length < STATUS_OPTIONS.length && selectedStatus.length > 0;
    const isDateFilterActive = !!(startDate || endDate);
    const isScheduleFilterActive =
      selectedSchedules.length < POSTING_SCHEDULE_OPTIONS.length && selectedSchedules.length > 0;
    const isPlatformFilterActive =
      selectedPlatforms.length < POSTING_PLATFORM_OPTIONS.length && selectedPlatforms.length > 0;
    const isPostTypeFilterActive =
      selectedPostTypes.length < POST_TYPE_OPTIONS.length && selectedPostTypes.length > 0;
    const isStatesFilterActive = selectedStatesFilter.length > 0;

    return locations.filter(loc => {
      const plan = loc.plan?.toLowerCase() || 'unknown';
      const status = loc.churned ? 'churned' : 'active';

      // Search filter always applies (AND with everything)
      if (query) {
        const name = loc.name?.toLowerCase() || '';
        const city = loc.city?.toLowerCase() || '';
        const state = loc.state?.toLowerCase() || '';
        const matchesSearch = name.includes(query) || city.includes(query) || state.includes(query);
        if (!matchesSearch) return false;
      }

      // Check each filter match
      const matchesPlan = selectedPlans.includes(plan);
      const matchesStatus = selectedStatus.includes(status);

      let matchesDate = true;
      if (isDateFilterActive) {
        const subDate = loc.subscriptionStartDate;
        if (!subDate) {
          matchesDate = false;
        } else {
          if (startDate && subDate < startDate) matchesDate = false;
          if (endDate && subDate > endDate) matchesDate = false;
        }
      }

      let matchesSchedule = true;
      if (isScheduleFilterActive) {
        const schedule = loc.postingSchedule || '';
        const isNotSet = !schedule;
        matchesSchedule = isNotSet
          ? selectedSchedules.includes('not_set')
          : selectedSchedules.includes(schedule);
      }

      let matchesPlatform = true;
      if (isPlatformFilterActive) {
        const platforms = loc.postingPlatforms || '';
        const isNotSet = !platforms;
        matchesPlatform = isNotSet
          ? selectedPlatforms.includes('not_set')
          : selectedPlatforms.some(p => p !== 'not_set' && platforms.includes(p));
      }

      let matchesPostType = true;
      if (isPostTypeFilterActive) {
        const postType = loc.postType || '';
        const isNotSet = !postType;
        matchesPostType = isNotSet
          ? selectedPostTypes.includes('not_set')
          : selectedPostTypes.includes(postType);
      }

      let matchesStates = true;
      if (isStatesFilterActive) {
        const normalizedLocState = normalizeStateToAbbrev(loc.state);
        matchesStates = selectedStatesFilter.includes(normalizedLocState);
      }

      // Apply filter mode logic
      if (filterMode === 'and') {
        if (isPlanFilterActive && !matchesPlan) return false;
        if (isStatusFilterActive && !matchesStatus) return false;
        if (isDateFilterActive && !matchesDate) return false;
        if (isScheduleFilterActive && !matchesSchedule) return false;
        if (isPlatformFilterActive && !matchesPlatform) return false;
        if (isPostTypeFilterActive && !matchesPostType) return false;
        if (isStatesFilterActive && !matchesStates) return false;
        return true;
      } else if (filterMode === 'or') {
        const activeFilters: boolean[] = [];
        if (isPlanFilterActive) activeFilters.push(matchesPlan);
        if (isStatusFilterActive) activeFilters.push(matchesStatus);
        if (isDateFilterActive) activeFilters.push(matchesDate);
        if (isScheduleFilterActive) activeFilters.push(matchesSchedule);
        if (isPlatformFilterActive) activeFilters.push(matchesPlatform);
        if (isPostTypeFilterActive) activeFilters.push(matchesPostType);
        if (isStatesFilterActive) activeFilters.push(matchesStates);

        if (activeFilters.length === 0) return true;
        return activeFilters.some(match => match);
      } else {
        // Custom mode: use individual filter relations
        const andFilters: boolean[] = [];
        const orFilters: boolean[] = [];

        if (isPlanFilterActive) {
          if (filterRelations.plan === 'and') andFilters.push(matchesPlan);
          else orFilters.push(matchesPlan);
        }
        if (isStatusFilterActive) {
          if (filterRelations.status === 'and') andFilters.push(matchesStatus);
          else orFilters.push(matchesStatus);
        }
        if (isDateFilterActive) {
          if (filterRelations.date === 'and') andFilters.push(matchesDate);
          else orFilters.push(matchesDate);
        }
        if (isScheduleFilterActive) {
          if (filterRelations.schedule === 'and') andFilters.push(matchesSchedule);
          else orFilters.push(matchesSchedule);
        }
        if (isPlatformFilterActive) {
          if (filterRelations.platform === 'and') andFilters.push(matchesPlatform);
          else orFilters.push(matchesPlatform);
        }
        if (isPostTypeFilterActive) {
          if (filterRelations.postType === 'and') andFilters.push(matchesPostType);
          else orFilters.push(matchesPostType);
        }
        if (isStatesFilterActive) {
          if (filterRelations.states === 'and') andFilters.push(matchesStates);
          else orFilters.push(matchesStates);
        }

        const passesAnd = andFilters.every(match => match);
        const passesOr = orFilters.length === 0 || orFilters.some(match => match);

        return passesAnd && passesOr;
      }
    });
  }, [
    locations,
    searchQuery,
    filterMode,
    filterRelations,
    selectedPlans,
    selectedStatus,
    startDate,
    endDate,
    selectedSchedules,
    selectedPlatforms,
    selectedPostTypes,
    selectedStatesFilter,
  ]);

  return {
    // Filter selections
    selectedPlans,
    setSelectedPlans,
    selectedStatus,
    setSelectedStatus,
    selectedSchedules,
    setSelectedSchedules,
    selectedPlatforms,
    setSelectedPlatforms,
    selectedPostTypes,
    setSelectedPostTypes,
    selectedStatesFilter,
    setSelectedStatesFilter,
    startDate,
    setStartDate,
    endDate,
    setEndDate,

    // Filter mode
    filterMode,
    setFilterMode,
    filterRelations,
    setFilterRelations,

    // Search
    searchQuery,
    setSearchQuery,
    statesFilterSearch,
    setStatesFilterSearch,

    // Colors
    colorBy,
    setColorBy,
    customColors,
    handleColorChange,

    // Derived data
    availableStates,
    filteredFilterStates,
    activeFilterCount,
    filteredLocations,
  };
};

export default useClinicMapFilters;
